import type {ReactElement} from 'react'
import {useMemo, Fragment} from 'react'
import {
  faLongArrowAltUp,
  faLongArrowAltDown,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import isNil from 'lodash/isNil'
import last from 'lodash/last'
import type {Column as ExternalColumn} from 'react-table'
import {useTable, useExpanded, useSortBy} from 'react-table'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import type {Alignment, Column, CellProps} from '@freckle/educator-materials/ts/logic/table/table'
import {
  expandableTableContainer,
  newStyleTableHeader,
  newStyleTableBody,
  expandableRow,
  table,
  expanderIcon,
  stripedRows,
} from './table.module.scss'

const DEFAULT_ALIGNMENT: Alignment = 'center'

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
type Row<TRow extends Object> = {
  original: TRow
  // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
  getToggleRowExpandedProps: () => Object
  isExpanded?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
type SubRowOptions<TRow extends Object> = {
  // Callback to trigger when a row is expanded.
  onExpandOrCollapse?: (row: TRow) => void
  // Specify `true` to allow sub-row expansion and contraction via any click
  // within the row.
  rowClickToggles?: true
}

// Handles putting props controlling our custom expand callback in the correct place
// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
function expandCallbackProps<TRow extends Object>(
  forLocation: 'expander' | 'row',
  row: {
    original: TRow
  },
  SubRow?: ((row: TRow) => ReactElement) | null,
  options?: SubRowOptions<TRow>,
) {
  const expandEnabled = !isNil(SubRow)
  const rowExpandEnabled = !isNil(options) && options.rowClickToggles === true

  if (
    expandEnabled &&
    ((rowExpandEnabled && forLocation === 'row') ||
      (!rowExpandEnabled && forLocation === 'expander')) &&
    !isNil(options) &&
    !isNil(options.onExpandOrCollapse)
  ) {
    return {onClick: () => options.onExpandOrCollapse?.(row.original)}
  }

  return {}
}

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
function rowExpandEnabled<TRow extends Object>(options?: SubRowOptions<TRow>) {
  return !isNil(options) && options.rowClickToggles === true
}

// Handles putting props controlling `react-table` expand components in the correct place
// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
function expandProps<TRow extends Object>(
  forLocation: 'expander' | 'row',
  row: {
    // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
    getToggleRowExpandedProps: () => Object
  },
  SubRow?: ((row: TRow) => ReactElement) | null,
  options?: SubRowOptions<TRow>,
) {
  const expandEnabled = !isNil(SubRow)

  if (
    expandEnabled &&
    ((rowExpandEnabled(options) && forLocation === 'row') ||
      (!rowExpandEnabled(options) && forLocation === 'expander'))
  ) {
    return row.getToggleRowExpandedProps()
  }

  return {}
}

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
type UseColumnsMemoParameters<TRow extends Object> = {
  columns: Column<TRow>[]
  SubRow?: (row: TRow) => ReactElement
  subRowOptions?: SubRowOptions<TRow>
}

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
function useColumnsMemo<TRow extends Object>(parameters: UseColumnsMemoParameters<TRow>) {
  const {SubRow, subRowOptions, columns} = parameters

  return useMemo(() => {
    const reducerInitialValue: Column<TRow>[] = isNil(SubRow)
      ? []
      : [
          {
            Header: '',
            id: '$educator-material-expander',
            Cell: (props: CellProps<TRow>) => {
              const row = props.row as unknown as Row<TRow>
              return (
                <div {...expandCallbackProps('expander', props.cell.row, SubRow, subRowOptions)}>
                  <div
                    {...expandProps('expander', row, SubRow, subRowOptions)}
                    data-test="expand-row-icon"
                  >
                    <FontAwesomeIcon
                      icon={row.isExpanded === true ? faChevronDown : faChevronRight}
                      className={`${expanderIcon} hidden-print`}
                    />
                  </div>
                </div>
              )
            },
            widthPx: 30,
          },
        ]

    const columnsReducerFunction = (results: Column<TRow>[], current: Column<TRow>) => {
      const prev = last(results)

      if (isNil(current.headerGroup)) {
        results.push(current)
      } else if (!isNil(prev) && !isNil(prev.columns) && prev.Header === current.headerGroup) {
        prev.columns.push(current)
      } else {
        results.push({
          Header: current.headerGroup,
          columns: [current],
        })
      }

      return results
    }

    return columns.reduce(columnsReducerFunction, reducerInitialValue)
  }, [SubRow, subRowOptions, columns])
}

// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
type Props<TRow extends Object> = {
  // Column definitions
  columns: Column<TRow>[]
  // Data to render.
  data: TRow[]
  // How to render sub-rows (expanded rows). If this is present, an expander
  // column will automatically be created as the first column in the table.
  SubRow?: (row: TRow) => ReactElement
  // Options for rendering sub-rows. Ignored unless `SubRow` is given.
  subRowOptions?: SubRowOptions<TRow>
  // Attribute for automated tests.
  dataTest?: string
  // Specify `true` to add stripes to alternating rows.
  striped?: true
}
// eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
export function Table<TRow extends Object>(props: Props<TRow>) {
  const {data, columns, dataTest, SubRow, striped, subRowOptions} = props

  const initialSort = useMemo(
    () =>
      columns.flatMap(column =>
        isNil(column.defaultSortOrder)
          ? []
          : [{id: column.id, desc: column.defaultSortOrder === 'desc'}],
      ),
    [columns],
  )

  const columnsMemo = useColumnsMemo({columns, SubRow, subRowOptions}) as ExternalColumn<TRow>[]

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns} =
    useTable(
      {
        columns: columnsMemo,
        data,
        disableSortRemove: true,
        initialState: {
          // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
          sortBy: initialSort,
        },
      },
      useSortBy,
      useExpanded,
    )

  return (
    <div data-test={dataTest} className={expandableTableContainer}>
      <table {...getTableProps()} className={table}>
        <thead className={newStyleTableHeader}>
          {headerGroups.map(headerGroup => {
            const {key, ...headerGroupProps} = headerGroup.getHeaderGroupProps()
            return (
              <tr {...headerGroupProps} key={key}>
                {headerGroup.headers.map(column => {
                  // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                  const {key, ...columnProps} = column.getHeaderProps(column.getSortByToggleProps())
                  return (
                    <th
                      // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                      key={key}
                      {...columnProps}
                      style={{
                        // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                        textAlign: column.alignment ?? DEFAULT_ALIGNMENT,
                        // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                        cursor: column.canSort === true ? 'pointer' : 'unset',
                        // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                        width: isNil(column.widthPx) ? 'unset' : `${column.widthPx}px`,
                      }}
                      {
                        // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                        ...(column.thClassName === undefined
                          ? {}
                          : // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                            {className: column.thClassName()})
                      }
                    >
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      {column.render('Header')}
                      {/* @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f */}
                      {column.canSort === true ? (
                        <span>
                          {' '}
                          {/* @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f */}
                          {column.isSorted === true ? (
                            <>
                              <FontAwesomeIcon
                                icon={
                                  // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                                  column.isSortedDesc === true
                                    ? faLongArrowAltDown
                                    : faLongArrowAltUp
                                }
                              />
                            </>
                          ) : null}
                        </span>
                      ) : null}
                    </th>
                  )
                })}
              </tr>
            )
          })}
        </thead>
        <tbody
          {...getTableBodyProps()}
          className={addMaybeClassName(newStyleTableBody, striped === true ? stripedRows : null)}
        >
          {rows.map((row, i) => {
            prepareRow(row)
            const {key, ...rowProps} = row.getRowProps()
            return (
              <Fragment key={key}>
                <tr
                  {...rowProps}
                  {...expandCallbackProps('row', row, SubRow, subRowOptions)}
                  className={rowExpandEnabled(subRowOptions) ? expandableRow : ''}
                  key={`row-${i}`}
                  data-test="table-row"
                >
                  {row.cells.map(cell => {
                    const {key, ...cellProps} = cell.getCellProps()
                    return (
                      <td
                        // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                        key={key}
                        {...cellProps}
                        // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                        {...expandProps('row', row, SubRow, subRowOptions)}
                        // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                        style={{textAlign: cell.column.alignment ?? DEFAULT_ALIGNMENT}}
                        className={
                          // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                          cell.column.tdClassName !== undefined
                            ? // @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f
                              cell.column.tdClassName(cell.row.original)
                            : ''
                        }
                      >
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
                {/* @ts-ignore https://app.asana.com/0/1203006199529183/1205040929211626/f */}
                {SubRow !== undefined && row.isExpanded === true ? (
                  <tr {...rowProps} key={`sub-row-${i}`}>
                    <td colSpan={visibleColumns.length}>
                      <SubRow {...row.original} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
